import { Action, PayloadAction } from '@reduxjs/toolkit';
import { all, call, put, select, take, takeEvery } from 'typed-redux-saga';

import { selectIsMeetingFullscreenEnabled } from '../../../../shared/features/config/selectors';
import { parentFrameCommunicator } from '../../../../shared/features/parent-frame-communicator';
import { emitterEventChannelFactory } from '../../../../shared/helpers/emitterEventChannelFactory';
import { countMetrika } from '../../../../shared/helpers/metrika';
import { OutgoingEvents } from '../../iframe-sagas/types';
import { cancelCallAction } from '../conference/slice';
import { setStoreDesktopSharingStatus } from '../screen-sharing/slice';
import {
    FullscreenAction,
    FullscreenResetReason,
    getFullscreenAction,
    getFullscreenResetAction,
    shouldCloseWindowedFullscreenByEscape,
} from './helpers';
import { selectIsFullscreen, setIsFullscreenAction, setStoreIsFullscreen } from './slice';

const sendFullscreenState = (isFullScreenEnabled: boolean) => {
    parentFrameCommunicator.sendMessage('*', {
        event: OutgoingEvents.SET_FULL_SCREEN,
        payload: { isFullScreenEnabled },
    });
};

const safeExitFullscreen = () => {
    if (!document.fullscreenElement) {
        return;
    }

    document.exitFullscreen().catch((error) => {
        // eslint-disable-next-line no-console
        console.warn('Error while exit fullscreen', error);
    });
};

/**
 * Сага перехода в состояние фуллскрина
 */
function* toFullscreenSaga() {
    // Если уже фулл-скрин - выйдем
    if (document.fullscreenElement) {
        return;
    }

    try {
        // Если вдруг не смогли перейти в фуллскрин - выход
        yield document.body.requestFullscreen();
        if (!document.fullscreenElement) {
            return;
        }

        countMetrika('meeting_screen', 'expand_during_sharing');

        yield* put(setStoreIsFullscreen(true));
        sendFullscreenState(true);

        // Дожидаемся события от документа о выходе из фуллскрина. Нам как минимум после вызова перехода в полноэкранный
        // режим прилетит событие "переход состоялся" (проигнорируем). А потом должны подождать событие о выходе из
        // полноэкранного режима по Esc или как реакция на вызов document.exitFullscreen
        const channel = emitterEventChannelFactory(document, 'fullscreenchange');

        try {
            while (true) {
                yield* take(channel);

                const isFullsreen = Boolean(document.fullscreenElement);

                if (!isFullsreen) {
                    break;
                }
            }
        } finally {
            channel.close();
        }

        yield* put(setStoreIsFullscreen(false));
        sendFullscreenState(false);

        countMetrika('meeting_screen', 'narrow_down_during_sharing');
    } catch (error) {
        // Запрещен фуллскрин или что-то подобное
        // eslint-disable-next-line no-console
        console.error('toFullscreenSaga error', error);
    }
}

function* setWindowedFullscreenSaga(newState: boolean) {
    yield* put(setStoreIsFullscreen(newState));
    sendFullscreenState(newState);

    if (!newState) {
        safeExitFullscreen();
    }
}

/**
 * Сага смены состояния фуллкрина
 */
function* setIsFullscreenSaga({ payload: newState }: PayloadAction<boolean>) {
    const isMeetingFullscreenEnabled = yield* select(selectIsMeetingFullscreenEnabled);
    const isFullscreen = yield* select(selectIsFullscreen);
    const domFullscreenState = Boolean(document.fullscreenElement);
    const fullscreenAction = getFullscreenAction({
        isMeetingFullscreenEnabled,
        isFullscreen,
        domFullscreenState,
        newState,
    });

    if (fullscreenAction === FullscreenAction.None) {
        return;
    }

    if (fullscreenAction === FullscreenAction.SetWindowed) {
        yield* call(setWindowedFullscreenSaga, newState);
    } else if (fullscreenAction === FullscreenAction.EnterBrowser) {
        yield* call(toFullscreenSaga);
    } else {
        sendFullscreenState(false);
        safeExitFullscreen();
    }
}

function* resetFullscreenOnExitSaga(reason: FullscreenResetReason) {
    const isMeetingFullscreenEnabled = yield* select(selectIsMeetingFullscreenEnabled);
    const isFullscreen = yield* select(selectIsFullscreen);
    const fullscreenAction = getFullscreenResetAction({
        isMeetingFullscreenEnabled,
        isFullscreen,
        reason,
    });

    if (fullscreenAction === FullscreenAction.SetWindowed) {
        yield* call(setWindowedFullscreenSaga, false);
        return;
    }

    safeExitFullscreen();
}

function* watchWindowedFullscreenEscapeSaga() {
    const isMeetingFullscreenEnabled = yield* select(selectIsMeetingFullscreenEnabled);

    if (!isMeetingFullscreenEnabled) {
        return;
    }

    const channel = emitterEventChannelFactory<[KeyboardEvent]>(document, 'keydown');

    try {
        while (true) {
            const [event] = yield* take(channel);
            const isFullscreen = yield* select(selectIsFullscreen);

            if (
                shouldCloseWindowedFullscreenByEscape({
                    isMeetingFullscreenEnabled,
                    isFullscreen,
                    key: event.key,
                })
            ) {
                yield* call(setWindowedFullscreenSaga, false);
            }
        }
    } finally {
        channel.close();
    }
}

/**
 * Корневая сага о фуллскрине
 */
export function* rootFullscreenSaga() {
    yield* all([
        takeEvery(setIsFullscreenAction, setIsFullscreenSaga),

        // При выходе из режима шаринга чистим только браузерный fullscreen.
        takeEvery(
            (action: Action) => {
                return setStoreDesktopSharingStatus.match(action) && action.payload === 'none';
            },
            resetFullscreenOnExitSaga,
            FullscreenResetReason.SharingEnded
        ),

        // При завершении звонка закрываем и новый оконный fullscreen встречи.
        takeEvery(cancelCallAction, resetFullscreenOnExitSaga, FullscreenResetReason.CallCancelled),

        watchWindowedFullscreenEscapeSaga(),
    ]);
}
